import { createFileRoute } from '@tanstack/react-router';
import Head from 'next/head';
import Link from 'next/link';
import { useEffect } from 'react';
import { motion, type Variants } from 'framer-motion';
import {
  Shield,
  Lock,
  Database,
  Eye,
  Server,
  FileText,
  Mail,
  Phone,
  ArrowRight,
  CheckCircle2,
  Ship,
  Globe,
  Radio,
  UserCheck,
} from 'lucide-react';
import { LandingTopBar } from '@/featured/landing-page/components/LandingTopBar';
import { LandingFooter } from '@/featured/landing-page/components/LandingFooter';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const SECTIONS = [
  {
    id: 'overview',
    title: '1. Privacy Commitment Overview',
    icon: Shield,
    content: (
      <>
        <p>
          At <strong>Missi Track</strong> (operated by <strong>PT Mitos International Solution</strong>),
          we respect your privacy and are committed to protecting the confidentiality, integrity, and security
          of personal information and maritime operational telemetry entrusted to us.
        </p>
        <p>
          This Privacy Policy explains how we collect, process, store, and safeguard your data when you access
          our web application, mobile interface, APIs, or utilize our vessel tracking hardware solutions.
        </p>
      </>
    ),
  },
  {
    id: 'data-we-collect',
    title: '2. Information We Collect',
    icon: Database,
    content: (
      <>
        <p>
          To deliver real-time vessel tracking, telemetry monitoring, and voyage management, we collect:
        </p>
        <div className="space-y-3 pt-1">
          <div className="p-3.5 rounded-xl bg-slate-800/40 border border-slate-700/50">
            <h4 className="font-semibold text-white text-xs mb-1">A. Customer Account & Contact Details</h4>
            <p className="text-slate-400 text-xs leading-relaxed">
              Customer name, corporate entity, street address, city, business telephone number, email address,
              and encrypted authentication credentials.
            </p>
          </div>

          <div className="p-3.5 rounded-xl bg-slate-800/40 border border-slate-700/50">
            <h4 className="font-semibold text-white text-xs mb-1">B. Vessel & Telemetry Data</h4>
            <p className="text-slate-400 text-xs leading-relaxed">
              Vessel identification (Vessel Name, MMSI, IMO number, Call Sign, Vessel Type), real-time GPS
              coordinates (Latitude, Longitude), Speed Over Ground (SOG), Course Over Ground (COG), true heading,
              and historical transit routes.
            </p>
          </div>

          <div className="p-3.5 rounded-xl bg-slate-800/40 border border-slate-700/50">
            <h4 className="font-semibold text-white text-xs mb-1">C. Marine Sensor & Hardware Diagnostics</h4>
            <p className="text-slate-400 text-xs leading-relaxed">
              Telemetry packets from onboard terminals (Iridium EdgeSolar, Globalstar, Orbcomm SC1000, Skywave),
              engine status (RPM, Hour Meter), battery voltage, power status, and geofence boundary crossing triggers.
            </p>
          </div>

          <div className="p-3.5 rounded-xl bg-slate-800/40 border border-slate-700/50">
            <h4 className="font-semibold text-white text-xs mb-1">D. System Usage & Audit Logs</h4>
            <p className="text-slate-400 text-xs leading-relaxed">
              IP addresses, browser configurations, mobile operating systems, notification logs, geofence alarm
              histories, and platform interaction timestamps.
            </p>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'how-we-use-data',
    title: '3. How We Use Your Information',
    icon: Ship,
    content: (
      <>
        <p>We utilize the collected information strictly for operational maritime purposes:</p>
        <ul className="list-disc pl-5 space-y-1.5 text-slate-400">
          <li>Delivering real-time vessel monitoring, live map display, and route trajectory calculations.</li>
          <li>Triggering automated safety notifications (geofence perimeter alerts, over-speed warnings, route deviation alarms).</li>
          <li>Providing historical voyage analysis, engine hour auditing, and exportable telemetry reports (CSV/PDF).</li>
          <li>Correlating live maritime meteorology (wind speeds, wave heights, atmospheric conditions) with vessel tracks.</li>
          <li>Providing technical customer support, diagnosing transponder telemetry issues, and system optimization.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'security',
    title: '4. Data Security and Encryption',
    icon: Lock,
    content: (
      <>
        <p>
          We employ robust technical and organizational security measures to prevent unauthorized access,
          alteration, or loss of your fleet data:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-slate-400">
          <li><strong>Encryption in Transit:</strong> All web and mobile communications use industry-standard TLS/SSL encryption.</li>
          <li><strong>Encryption at Rest:</strong> Database records and sensitive telemetry files are encrypted on secure servers.</li>
          <li><strong>Role-Based Access Control (RBAC):</strong> Multi-tenant architecture ensures each customer can only access their authorized fleet.</li>
          <li><strong>Credential Hashing:</strong> Passwords are cryptographically hashed using salted, one-way hash algorithms.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'sharing',
    title: '5. Third-Party Disclosures & Non-Sale of Data',
    icon: Globe,
    content: (
      <>
        <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs leading-relaxed mb-3">
          <strong>NO DATA MONETIZATION:</strong> We do <strong>NOT</strong> sell, rent, lease, or monetize your
          vessel positions, cargo routes, or personal information to third-party advertisers or data brokers.
        </div>
        <p>Data is shared only in the following necessary operational contexts:</p>
        <ul className="list-disc pl-5 space-y-1.5 text-slate-400">
          <li><strong>Satellite & Cellular Carriers:</strong> To transmit raw telemetry packets from onboard hardware (Iridium, Globalstar, Orbcomm, cellular GSM).</li>
          <li><strong>Mapping Tile Providers:</strong> Map background layers (OpenStreetMap, satellite layers) display data locally on your client without transmitting identifiable vessel ownership.</li>
          <li><strong>Regulatory & Legal Authorities:</strong> If required by mandatory court orders, maritime safety regulations, search-and-rescue operations (BASARNAS), or applicable Indonesian statutory law.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'retention',
    title: '6. Data Retention and Deletion Rights',
    icon: Server,
    content: (
      <>
        <p>
          We retain vessel telemetry and account records for as long as your account remains active and in accordance
          with statutory maritime recordkeeping obligations.
        </p>
        <p>
          Customers have the right to request access to their telemetry history, export reports, or request permanent
          deletion of their account data upon termination of service by submitting a written request to{' '}
          <a href="mailto:support@mitos.id" className="text-blue-400 underline">support@mitos.id</a>.
        </p>
      </>
    ),
  },
  {
    id: 'cookies',
    title: '7. Cookies and Local Storage',
    icon: Eye,
    content: (
      <>
        <p>
          We use strictly necessary cookies and browser local storage (e.g., UI theme preferences, map display modes,
          and secure authentication tokens) to maintain your session and deliver an optimal user experience. We do
          not employ intrusive third-party cross-site advertising cookies.
        </p>
      </>
    ),
  },
  {
    id: 'contact-privacy',
    title: '8. Privacy Inquiries & Data Protection Contact',
    icon: UserCheck,
    content: (
      <>
        <p>
          If you have questions, concerns, or requests regarding this Privacy Policy or how your fleet telemetry
          is handled, please reach out to our team:
        </p>
        <div className="p-4 rounded-xl bg-slate-800/60 border border-slate-700/50 space-y-1 text-xs text-slate-300 mt-2">
          <p className="font-bold text-white">PT Mitos International Solution — Data Protection</p>
          <p>Perkantoran Tanjung Mas Raya, blok B1 nomor 44, RT.002/RW.001.</p>
          <p>Tanjung Barat, Jagakarsa, Jakarta Selatan, Indonesia</p>
          <p className="pt-1 text-blue-400">Email: support@mitos.id</p>
          <p className="text-slate-400">Phone: 021 2787 4648</p>
        </div>
      </>
    ),
  },
];

export default function PrivacyPage() {
  useEffect(() => {
    document.documentElement.setAttribute('dir', 'ltr');
    document.body.style.backgroundColor = '#070e1b';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  return (
    <>
      <Head>
        <title>Privacy Policy — Missi Track</title>
        <meta
          name="description"
          content="Privacy Policy for Missi Track. Learn how we collect, protect, and encrypt vessel telemetry and account data."
        />
        <meta property="og:title" content="Privacy Policy — Missi Track" />
        <meta
          property="og:description"
          content="Detailed privacy policy regarding maritime telemetry, vessel positions, and customer security on Missi Track."
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Archivo+Black&family=Inter:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Head>

      <main
        className="landing min-h-screen text-slate-200 overflow-x-hidden relative"
        style={{ backgroundColor: '#070e1b' }}
      >
        <LandingTopBar />

        {/* Hero Header */}
        <section className="relative -mt-24 pt-36 pb-16 px-4 overflow-hidden border-b border-slate-800 bg-[#070e1b]">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-blue-500/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-4xl mx-auto text-center space-y-4">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-semibold"
            >
              <Shield className="w-3.5 h-3.5" />
              <span>Data Protection & Privacy</span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight"
            >
              Privacy Policy
            </motion.h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto"
            >
              Learn how Missi Track protects your fleet telemetry, coordinates, sensor records, and account data.
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="text-xs text-slate-500 pt-2"
            >
              Last Updated: September 19, 2026 • Effective Date: January 1, 2026
            </motion.div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            {SECTIONS.map((sec) => {
              const Icon = sec.icon;
              return (
                <div
                  key={sec.id}
                  id={sec.id}
                  className="p-6 sm:p-8 rounded-2xl bg-slate-900/60 border border-slate-800 shadow-sm space-y-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h2 className="text-lg sm:text-xl font-bold text-white">{sec.title}</h2>
                  </div>
                  <div className="text-sm leading-relaxed text-slate-300 space-y-3 pl-0 sm:pl-13">
                    {sec.content}
                  </div>
                </div>
              );
            })}

            {/* Contact Card */}
            <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-blue-950/40 to-slate-900/80 border border-blue-800/40 space-y-4">
              <h3 className="text-base sm:text-lg font-bold text-white">Need Clarification on Data Privacy?</h3>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                We are dedicated to transparent data practices. If you have any inquiries regarding data processing,
                security certifications, or telemetry deletion requests, please contact us:
              </p>
              <div className="grid sm:grid-cols-2 gap-3 pt-2 text-xs">
                <div className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-800/60 border border-slate-700/50">
                  <Mail className="w-4 h-4 text-blue-400 shrink-0" />
                  <div>
                    <span className="text-slate-400 block text-[10px]">Email Support</span>
                    <a href="mailto:support@mitos.id" className="text-white font-medium hover:text-blue-400">
                      support@mitos.id
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-800/60 border border-slate-700/50">
                  <Phone className="w-4 h-4 text-emerald-400 shrink-0" />
                  <div>
                    <span className="text-slate-400 block text-[10px]">Phone</span>
                    <a href="tel:+622127874648" className="text-white font-medium hover:text-emerald-400">
                      021 2787 4648
                    </a>
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-400 border-t border-slate-800/80 pt-3">
                <span className="font-semibold text-slate-300">Office Address: </span>
                Perkantoran Tanjung Mas Raya, blok B1 nomor 44, RT.002/RW.001. Tanjung Barat, Jagakarsa, Jakarta Selatan.
              </div>
            </div>

            {/* Link to Terms */}
            <div className="text-center pt-4">
              <Link
                href="/terms"
                className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                <span>Read our Terms & Conditions</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        <LandingFooter />
      </main>
    </>
  );
}

PrivacyPage.guestGuard = false;
PrivacyPage.authGuard = false;

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
});
